import { constants as cryptoConstants } from './constants';
import { getDateObj } from './utils';
import { Certificate } from './Certificate';
import {
    OpenStoreError,
    GetCertificateListError,
    EmptyCertificateListError,
    ProcessingCertificateListError,
    ThumbprintNotSpecifiedError,
    CertificateNotFoundError,
    GetCertificateForHashError,
    InstallHashDataError,
    InstallSignDataError,
    InstallSignSettingsError,
    CreateSignError,
} from './errors';
import { call } from './api';


const canAsync = () => !!window.cadesplugin.CreateObjectAsync;

const createObject = async args =>
    (window.cadesplugin.CreateObjectAsync || window.cadesplugin.CreateObject)(args);

const propset = async (object, property, value) => {
    if (canAsync())
        await object[`propset_${property}`](value);
    else
        object[property] = value;
};

const createAndOpenStore = async () => {
    const store = await createObject('CAdESCOM.Store');
    try {
        await store.Open(
            window.cadesplugin.CAPICOM_CURRENT_USER_STORE,
            window.cadesplugin.CAPICOM_MY_STORE,
            window.cadesplugin.CAPICOM_STORE_OPEN_MAXIMUM_ALLOWED,
        );
        return store;
    } catch ({ message }) {
        throw new OpenStoreError(message);
    }
};

const getCerts = async store => {
    try {
        let certs = await store.Certificates;
        if (certs) {
            certs = await certs.Find(
                window.cadesplugin.CAPICOM_CERTIFICATE_FIND_TIME_VALID,
            );
            /**
             * Не рассматриваются сертификаты, в которых отсутствует закрытый ключ
             * или не действительны на данный момент
             * */
            certs = await certs.Find(
                window.cadesplugin.CAPICOM_CERTIFICATE_FIND_EXTENDED_PROPERTY,
                cryptoConstants.PropId.CAPICOM_PROPID_KEY_PROV_INFO,
            );
        }
        return certs;
    } catch ({ message }) {
        throw new GetCertificateListError(message);
    }
};

const getCount = async certs => {
    const count = await certs.Count;
    if (!count)
        throw new EmptyCertificateListError();
    return count;
};

const getCertificateItem = async (certs, number) => {
    const item = await certs.Item(number);
    return new Certificate({
        _cert: await item,
        thumbprint: await item.Thumbprint,
        subjectName: await item.SubjectName,
        issuerName: await item.IssuerName,
        validFrom: await item.ValidFromDate,
        validTo: await item.ValidToDate,
    });
};

const getCertificateItemList = async (certs, count) => {
    try {
        const list = [];
        for (let i = 0; i < count; i++)
            list.push(getCertificateItem(certs, i + 1));
        return await Promise.all(list);
    } catch ({ message }) {
        throw new ProcessingCertificateListError(message);
    }
};


export const getCertsList = call(async () => {
    const store = await createAndOpenStore();
    const certs = await getCerts(store);
    const count = await getCount(certs);
    const result = await getCertificateItemList(certs, count);
    store.Close();
    return result;
});

export const getCert = call(async thumbprint => {
    if (!thumbprint)
        throw new ThumbprintNotSpecifiedError();
    const list = await getCertsList();
    const cert = list.find(cert => cert.thumbprint === thumbprint);
    if (!cert)
        throw new CertificateNotFoundError(thumbprint);
    return cert;
});

const getCadesCert = async hash => {
    const store = await createAndOpenStore();
    const certs = await store.Certificates;
    const filtered = await certs.Find(window.cadesplugin.CAPICOM_CERTIFICATE_FIND_SHA1_HASH, hash);
    if (!filtered.Count)
        throw new GetCertificateForHashError(hash);
    const item = await filtered.Item(1);
    store.Close();
    return item;
};

const installSignatureData = async (attrs, clientTime) => {
    try {
        await propset(attrs, 'Name', cryptoConstants.Time.AUTHENTICATED_ATTRIBUTE_SIGNING_TIME);
        await propset(attrs, 'Value', clientTime);
    } catch ({ message }) {
        throw new InstallSignDataError(message);
    }
};

const installHashData = async (hashedData, hashAlg, dataHash) => {
    try {
        await propset(hashedData, 'Algorithm', hashAlg);
        await hashedData.SetHashValue(dataHash);
    } catch ({ message }) {
        throw new InstallHashDataError(message);
    }
};

const installSignatureSettings = async (signer, cert, oAttrs) => {
    try {
        await propset(signer, 'Certificate', cert);
        const attrs = await signer.AuthenticatedAttributes2;
        await attrs.Add(oAttrs);
        await propset(signer, 'Options',
            window.cadesplugin.CAPICOM_CERTIFICATE_INCLUDE_END_ENTITY_ONLY);
    } catch ({ message }) {
        throw new InstallSignSettingsError(message);
    }
};

const createSignature = async (oSignedData, oHashedData, oSigner) => {
    try {
        const signature = await oSignedData.SignHash(
            oHashedData,
            oSigner,
            window.cadesplugin.CADESCOM_CADES_BES,
        );
        return signature;
    } catch ({ message }) {
        throw new CreateSignError(message);
    }
};

export const signHash = call(async (hash, dataHash, hashAlg) => {
    const cert = await getCadesCert(hash);
    const clientTime = getDateObj(new Date());
    const oAttrs = await createObject('CADESCOM.CPAttribute');
    const oHashedData = await createObject('CAdESCOM.HashedData');
    const oSignedData = await createObject('CAdESCOM.CadesSignedData');
    const oSigner = await createObject('CAdESCOM.CPSigner');

    await installSignatureData(oAttrs, clientTime);
    await installHashData(oHashedData, hashAlg, dataHash);
    await installSignatureSettings(oSigner, cert, oAttrs);
    return createSignature(oSignedData, oHashedData, oSigner);
});
